// GENERATED by scripts/generate-route-latency-budget.mjs from packages/kernel-client/src/routeLatencyBudget.ts
// and packages/kernel-client/src/generated/routes.generated.ts.
// DO NOT EDIT. Run `npm run route-budget:codegen` to regenerate.
//
// WP-575 (TEN-2917, TEN-1126), commits_to DEC-64. This file is emitted IDENTICALLY into
// packages/mcp-server and packages/cli so the two connectors cannot disagree about how
// long a gateway route may take — the defect this work package removes was exactly that
// disagreement (MCP hardcoded a blanket 10s, the CLI set none at all).
//
// The rationale — why route TYPE is the structural predictor of LLM exposure, and how
// each value is grounded in DEC-64 rather than invented — lives with the declaration in
// packages/kernel-client/src/routeLatencyBudget.ts. Read that file before changing a
// number here; changing it HERE does nothing, the next codegen run overwrites it.
//
// Why a copy and not an import: both connectors consume @productbrain/kernel-client
// TYPE-ONLY (mcp-server via a tsconfig paths alias for Railway's isolated Docker build,
// DEC-701; cli as a devDependency it must not emit into its published dist/). A budget
// sets an AbortSignal, so it is runtime code and cannot be type-erased. See the
// generator header for the full constraint.

export type GatewayRouteType = 'query' | 'mutation' | 'action';

/** The budget table, copied verbatim from the SSOT. Milliseconds. */
export const ROUTE_LATENCY_BUDGET_MS = {
	query: 10_000,
	mutation: 10_000,
	action: 30_000,
} as const;

/**
 * PER-ROUTE OVERRIDES — routes whose OWN server-side deadline exceeds their type's budget
 * (PR #533 review, Codex P1). Route type predicts LLM EXPOSURE, not LLM COUNT: a flat
 * action budget assumed every action is one inline LLM call, and shipped actions declare
 * far longer deadlines. Bounding those at the type budget aborts a call the server is
 * still legitimately working on. Each value is derived from a deadline the SERVER
 * declares — see the SSOT for the citation behind every number.
 */
export const ROUTE_LATENCY_BUDGET_OVERRIDE_MS = {
	// convex/intelligence/onboardingChat.ts:333 declares `timeoutMs: 45_000` for the extraction
	// LLM call — 15s BEYOND the flat action budget. 60s clears it with room for the surrounding
	// request/response work the 45s covers none of.
	'onboarding.chat': 60_000,
	// convex/intelligence/spineCheck.ts:210 sets SPINE_CHECK_WALL_CLOCK_BUDGET_MS = 4 * 60_000
	// for its SEQUENTIAL strategy probes (:225-239, deliberately not Promise.all), and :215
	// reserves a tail of one 25s probe + an optional 25s story pass + 10s. The server's own
	// design ceiling is scheduleSpineCheck's 5-min MAX_RUN_IN_FLIGHT_MS lease (:206-209).
	// 5.5 min sits above that ceiling and far under Convex's 10-min action cap.
	'quality.spineCheck': 330_000,
} as const;

/**
 * The widest declared budget — the fail-safe default for a route name this map does not
 * know. Derived from the tables rather than restated, so it cannot drift if a value moves.
 * Spans the overrides too, so the unknown-route default is never TIGHTER than a budget
 * already granted to a known route.
 */
export const WIDEST_ROUTE_LATENCY_BUDGET_MS: number = Math.max(
	...Object.values(ROUTE_LATENCY_BUDGET_MS),
	...Object.values(ROUTE_LATENCY_BUDGET_OVERRIDE_MS),
);

/** Every gateway route's Convex function type, from convex/http.ts's registry. */
const ROUTE_TYPE_ENTRIES: Readonly<Record<string, GatewayRouteType>> = {
	"resolveWorkspace": "query",
	"feedback.submit": "mutation",
	"feedback.listOwn": "query",
	"feedback.list": "action",
	"feedback.note": "action",
	"feedback.group": "action",
	"feedback.status": "action",
	"organisation.status": "query",
	"organisation.assignMembership": "mutation",
	"organisation.removeMembership": "mutation",
	"organisation.setOwnership": "mutation",
	"organisation.clearOwnership": "mutation",
	"organisation.activate": "mutation",
	"organisation.upgrade": "mutation",
	"organisation.upgradeRung2": "mutation",
	"organisation.upgradeRung3": "mutation",
	"organisation.upgradeRung4": "mutation",
	"organisation.updateGovernanceMode": "mutation",
	"organisation.hierarchy.show": "query",
	"organisation.hierarchy.setParent": "mutation",
	"organisation.hierarchy.clearParent": "mutation",
	"acceptPolicy.approve": "mutation",
	"acceptPolicy.retire": "mutation",
	"acceptPolicy.list": "query",
	"chain.seed": "mutation",
	"chain.listCollections": "query",
	"chain.getCollection": "query",
	"chain.getCollectionFields": "query",
	"chain.auditCollections": "query",
	"chain.exportDefinitions": "query",
	"chain.updateCollection": "mutation",
	"chain.listEntries": "query",
	"chain.getEntry": "query",
	"chain.getQualityVerdict": "query",
	"chain.batchGetEntries": "query",
	"chain.createEntry": "action",
	"chain.updateEntry": "mutation",
	"chain.restoreArchivedEntry": "mutation",
	"chain.moveToCollection": "mutation",
	"chain.shapeAdvisories": "query",
	"chain.shapeAdvisorySummary": "query",
	"chain.showShapeAdvisory": "query",
	"chain.dispositionShapeAdvisory": "mutation",
	"conflicts.list": "query",
	"conflicts.resolve": "mutation",
	"conflicts.summary": "query",
	"conflicts.snooze": "mutation",
	"conflicts.reverdict": "mutation",
	"direction.list": "query",
	"direction.refresh": "action",
	"direction.defer": "mutation",
	"question.create": "mutation",
	"question.adopt": "mutation",
	"question.assign": "mutation",
	"question.snooze": "mutation",
	"question.decline": "mutation",
	"question.answer": "mutation",
	"question.forceClose": "mutation",
	"question.list": "query",
	"chain.classifyCollection": "action",
	"chain.classifyStrategyCategory": "query",
	"chain.batchClassifyHeuristic": "query",
	"chain.resolveCollection": "action",
	"chain.searchEntries": "query",
	"chain.searchByCanonicalName": "query",
	"chain.commitEntry": "mutation",
	"chain.verifyEntry": "mutation",
	"chain.batchCommitConstellation": "mutation",
	"chain.listEntryHistory": "query",
	"chain.listEntryVersions": "query",
	"chain.createEntryRelation": "mutation",
	"chain.createEntryRelations": "mutation",
	"chain.removeEntryRelation": "mutation",
	"chain.normalizeEntryDataLLM": "action",
	"chain.decomposeContent": "action",
	"chain.normalizeEntryDataPreview": "action",
	"chain.listEntryRelations": "query",
	"chain.scoreLinkCandidates": "query",
	"chain.evaluateCoherence": "query",
	"chain.listAutoLinkSuggestions": "query",
	"chain.acceptAutoLinkSuggestion": "mutation",
	"chain.dismissAutoLinkSuggestion": "mutation",
	"chain.quarantineAutoLinkSuggestion": "mutation",
	"chain.resurrectAutoLinkSuggestion": "mutation",
	"chain.expireAutoLinkSuggestion": "mutation",
	"chain.clusterAutoLinkSuggestions": "query",
	"chain.batchApplyAutoLinkSuggestions": "action",
	"chain.validateCommitConstellation": "query",
	"chain.getCaptureContract": "query",
	"chain.gatherContext": "query",
	"chain.getConstellation": "query",
	"chain.auditBet": "query",
	"agentKnowledge.facilitateEnvelope": "action",
	"agentKnowledge.wrapupEnvelope": "action",
	"agentKnowledge.captureEnvelope": "action",
	"chain.graphSuggestLinks": "query",
	"chain.graphGatherContext": "query",
	"chain.assembleBuildContext": "query",
	"chain.qualityCheck": "query",
	"chain.changeDetection": "query",
	"chain.structuralAggregation": "query",
	"chain.detectSemanticConflicts": "action",
	"chain.taskAwareGatherContext": "query",
	"chain.journeyAwareGatherContext": "query",
	"chain.resolveTaskStartup": "query",
	"chain.getBindingGovernanceView": "query",
	"chain.resolveTaskStartupHybrid": "action",
	"chain.taskAwareHybridGatherContext": "action",
	"chain.gatherFromSeeds": "query",
	"chain.getEntryNeighborhood": "query",
	"chain.deepChainWalk": "action",
	"chain.recordBriefRun": "mutation",
	"chain.getLastBriefRun": "query",
	"chain.incrementalChanges": "query",
	"chain.compoundQuery": "action",
	"chain.dismissSuggestion": "mutation",
	"chain.recordSessionSignal": "mutation",
	"chain.workspaceReadiness": "query",
	"chain.getCaptureHealth": "query",
	"chain.getGroundingHealth": "query",
	"chain.recordGroundingOutcome": "mutation",
	"chain.suggestLinksForCapture": "query",
	"chain.setOnboardingCompleted": "mutation",
	"chain.checkCardinalityWarning": "query",
	"chain.ingestDocument": "action",
	"chain.getOrientEntries": "query",
	"chain.getOrientView": "action",
	"chain.getRitualsSurface": "query",
	"chain.getGovernanceWithRelations": "action",
	"chain.classifyGovernance": "query",
	"chain.getVocabulary": "query",
	"scoreboard.get": "action",
	"rework.report": "mutation",
	"chain.listLabels": "query",
	"chain.createLabel": "mutation",
	"chain.updateLabel": "mutation",
	"chain.deleteLabel": "mutation",
	"chain.applyLabel": "mutation",
	"chain.removeLabel": "mutation",
	"chain.listEntriesByLabel": "query",
	"setup.getActiveSurface": "query",
	"setup.materializeSetup": "action",
	"setup.recordTamperRefusal": "mutation",
	"setup.recordTransition": "mutation",
	"setup.getCurrentSetupState": "query",
	"setup.listAssetsForUser": "query",
	"setup.ingestSetupAsset": "mutation",
	"setup.ingestSetupAssetWithBody": "action",
	"setup.fetchAssetBody": "action",
	"setup.auditAssetBodies": "action",
	"setup.repairAssetBody": "action",
	"setup.listFailedAuditReceipts": "action",
	"setup.markPersonalSetupAssetDormantFromSync": "mutation",
	"setup.resolveSemanticRefs": "query",
	"setup.updateLastProjectedHash": "mutation",
	"setup.getSkillSystemHealth": "query",
	"setup.recordActivationReceipt": "mutation",
	"setup.recordSetupInvocation": "mutation",
	"setup.getUserActivationState": "query",
	"setup.getPbSetupState": "query",
	"setup.getSkillBody": "action",
	"setup.stampMcpOnlySurface": "mutation",
	"setup.stampDetectedSurfaces": "mutation",
	"setup.getKey32Snapshot": "query",
	"setup.getKey33Snapshot": "query",
	"gaps.record": "mutation",
	"gaps.resolve": "mutation",
	"gaps.top": "query",
	"gaps.stats": "query",
	"agent.startSession": "mutation",
	"agent.resumeSession": "mutation",
	"agent.closeSession": "mutation",
	"agent.markOriented": "mutation",
	"agent.touchSession": "mutation",
	"agent.recordActivity": "mutation",
	"agent.getSession": "query",
	"agent.getActiveSession": "query",
	"agent.recentSessions": "query",
	"agent.activityStats": "query",
	"agent.getActivityByDay": "query",
	"agent.validateSession": "query",
	"agent.getSessionWrapup": "query",
	"agent.recordWrapup": "mutation",
	"agent.reportOrientMetric": "mutation",
	"agent.listSessions": "query",
	"agent.showConversation": "query",
	"usage.getWorkspaceSummary": "query",
	"quality.evaluateHeuristicAndSchedule": "mutation",
	"quality.reEvaluateEntry": "mutation",
	"quality.evaluateAtCapture": "action",
	"quality.evaluateAtCommit": "action",
	"quality.evaluateForReview": "action",
	"quality.getCachedVerdict": "query",
	"quality.getLatestVerdictForEntry": "query",
	"quality.spineCheck": "action",
	"quality.scheduleSpineCheck": "mutation",
	"quality.getLatestSpineVerdict": "query",
	"onboarding.chat": "action",
	"workspace.health": "query",
	"workspace.healthAll": "query",
	"workspace.backfill": "mutation",
	"workspace.backfillAll": "mutation",
	"authorityDomains.readiness": "query",
	"authorityDomains.add": "mutation",
	"authorityDomains.propose": "action",
	"authorityDomains.review": "query",
	"authorityDomains.queueKnownTag": "mutation",
	"authorityDomains.ratify": "mutation",
	"authorityDomains.reject": "mutation",
	"authorityDomains.discardPending": "mutation",
	"authorityDomains.recordSample": "mutation",
	"authorityDomains.benchmark": "action",
	"authorityDomains.activateCutover": "mutation",
	"authorityDomains.principleDistribution": "query",
	"performance.getVitalsSummary": "query",
	"performance.getApiOverview": "query",
	"performance.getRouteBreakdown": "query",
	"performance.getSlowSamples": "query",
	"performance.getSampleCount": "query",
	"chainwork.listTypes": "query",
	"chainwork.getChainType": "query",
	"chainwork.scoreRun": "query",
	"chainwork.getArtifact": "query",
	"chainwork.getWorkflowRun": "query",
	"chainwork.getLatestWorkflowRun": "query",
	"chainwork.recordWorkflowCheckpoint": "mutation",
	"chainwork.finalizeWorkflowRun": "action",
	"chainwork.submitToKG": "action",
	"chainwork.generate": "action",
	"chainwork.getLastVerifiedBrief": "query",
	"gitchain.createChain": "mutation",
	"gitchain.editLink": "mutation",
	"gitchain.updateChain": "mutation",
	"gitchain.getChain": "query",
	"gitchain.listChains": "query",
	"gitchain.getHistory": "query",
	"gitchain.listCommits": "query",
	"gitchain.commitChain": "mutation",
	"gitchain.diffVersions": "mutation",
	"gitchain.runGate": "query",
	"gitchain.createBranch": "mutation",
	"gitchain.listBranches": "mutation",
	"gitchain.checkConflicts": "mutation",
	"gitchain.mergeBranch": "mutation",
	"gitchain.addComment": "mutation",
	"gitchain.resolveComment": "mutation",
	"gitchain.listComments": "mutation",
	"gitchain.revertChain": "mutation",
	"staging.getCommittedSourceRefs": "query",
	"staging.commitStagingEntryWithClassification": "action",
	"governance.listProposals": "query",
	"governance.countOpenProposals": "query",
	"governance.respondToProposal": "mutation",
};

/**
 * NULL-PROTOTYPE, deliberately (PR #533 review, Codex P2).
 *
 * A plain object literal inherits Object.prototype, so an unknown route name that
 * collides with an inherited member — `toString`, `constructor`, `valueOf` — resolves to a
 * TRUTHY function instead of falling through to the widest-budget default below. That
 * yielded `AbortSignal.timeout(undefined)`, which THROWS before the call ever reaches the
 * gateway — turning the fail-safe default into a hard failure.
 *
 * The SSOT scans an array (`GATEWAY_ROUTES.find`) and has no such hole, so a plain-object
 * copy DISAGREED with the source it is generated from — precisely the connector drift this
 * file exists to end. A null prototype restores parity for every consumer of the map at
 * once (`latencyBudgetMsForRoute` here, `routeMayMutate` in both connectors' seams) rather
 * than guarding each lookup and leaving the next one to rediscover it.
 */
export const ROUTE_TYPE_BY_NAME: Readonly<Record<string, GatewayRouteType>> = Object.assign(
	Object.create(null) as Record<string, GatewayRouteType>,
	ROUTE_TYPE_ENTRIES,
);

/** Budget for a route TYPE. Total over the three kinds the registry can produce. */
export function latencyBudgetMsForRouteType(type: GatewayRouteType): number {
	return ROUTE_LATENCY_BUDGET_MS[type];
}

/**
 * Budget for a route by NAME.
 *
 * FAILS SAFE, DELIBERATELY: an unknown name gets the WIDEST budget, not the tightest and
 * not a throw. Guessing low on a route we cannot classify would abort a call still
 * legitimately in flight and re-create the exact false-failure this work package removes.
 * Guessing high only delays surfacing a genuinely stuck call — and an unknown name is
 * almost always a caller bug the gateway rejects in milliseconds anyway.
 */
export function latencyBudgetMsForRoute(routeName: string): number {
	// `Object.hasOwn`, not truthiness: the override table is a plain literal, so a bare
	// lookup would resolve INHERITED members to a truthy value. Same hole the null
	// prototype closes above, guarded here because this table stays a plain literal.
	if (Object.hasOwn(ROUTE_LATENCY_BUDGET_OVERRIDE_MS, routeName)) {
		return (ROUTE_LATENCY_BUDGET_OVERRIDE_MS as Record<string, number>)[routeName];
	}
	const type = ROUTE_TYPE_BY_NAME[routeName];
	return type ? latencyBudgetMsForRouteType(type) : WIDEST_ROUTE_LATENCY_BUDGET_MS;
}
